<template>
  <div class="hk-page">
    <!-- ===== HERO ===== -->
    <section class="hk-hero">
      <div class="hk-hero__blob hk-hero__blob--1"></div>
      <div class="hk-hero__blob hk-hero__blob--2"></div>
      <div class="section-container">
        <div class="hk-hero__grid">
          <div class="hk-hero__content">
            <span class="hk-badge hk-badge--light">
              <span class="hk-badge__dot"></span>
              Homekind App · Closed Beta
            </span>
            <h1 class="hk-hero__title">
              Satu aplikasi untuk <span class="hk-hl hk-hl--light">semua urusan rumah</span>
            </h1>
            <p class="hk-hero__subtitle">
              Keuangan, tagihan, agenda, daftar belanja, perawatan rumah, hingga obrolan keluarga, semuanya di satu
              tempat yang dipakai bersama seisi rumah. Dibuat khusus untuk keluarga Indonesia.
            </p>
            <div class="hk-hero__actions">
              <router-link :to="betaLink" class="hk-btn hk-btn--primary">
                Gabung Beta Gratis <i class="fa fa-arrow-right"></i>
              </router-link>
              <router-link to="/homekind#fitur" class="hk-btn hk-btn--ghost-light">Lihat Fitur</router-link>
            </div>
            <div class="hk-hero__platforms">
              <span><i class="fa fa-android"></i> Android</span>
              <span><i class="fa fa-apple"></i> iOS</span>
              <span><i class="fa fa-globe"></i> Web</span>
            </div>
          </div>

          <div class="hk-hero__visual">
            <div class="hk-hero__arch"></div>
            <IlloLamp class="hk-hero__lamp" />
            <IlloPlant class="hk-hero__plant" />
            <div class="hk-hero__phone hk-hero__phone--back">
              <PhoneMockup screen="agenda" compact />
            </div>
            <div class="hk-hero__phone hk-hero__phone--front">
              <PhoneMockup screen="home" />
            </div>
            <div class="hk-float hk-float--1">
              <div class="hk-float__icon"><i class="fa fa-check"></i></div>
              <div>
                <strong>Tagihan PLN lunas</strong>
                <span>Rp489.000 · tercatat ke Kas</span>
              </div>
            </div>
            <div class="hk-float hk-float--2">
              <div class="hk-float__icon hk-float__icon--alt"><i class="fa fa-bell"></i></div>
              <div>
                <strong>Servis AC 3 hari lagi</strong>
                <span>Ditugaskan ke Pak Joko</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <svg class="hk-wave" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 48c180 26 360 34 540 18S900 6 1080 14s270 30 360 34v32H0Z" />
      </svg>
    </section>

    <!-- ===== PROBLEM ===== -->
    <section class="hk-section hk-section--linen">
      <div class="section-container">
        <div class="hk-title animate-on-scroll">
          <span class="hk-badge">
            <span class="hk-badge__dot"></span>
            Kenapa Homekind?
          </span>
          <h2>Urusan rumah tersebar di <span class="hk-hl">terlalu banyak tempat</span></h2>
          <p class="hk-title__sub">
            Grup WhatsApp, aplikasi catatan, m-banking, spreadsheet, dan ingatan masing-masing. Akibatnya ada yang
            terlewat.
          </p>
        </div>
        <div class="hk-problem__grid">
          <div v-for="(p, index) in problems" :key="p.title" class="hk-problem animate-on-scroll"
            :class="'delay-' + (index + 1)">
            <div class="hk-problem__icon"><i :class="p.icon"></i></div>
            <h4>{{ p.title }}</h4>
            <div class="hk-problem__text">{{ p.text }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FEATURES ===== -->
    <section id="fitur" class="hk-section hk-section--linen-alt">
      <div class="section-container">
        <div class="hk-title animate-on-scroll">
          <span class="hk-badge">
            <span class="hk-badge__dot"></span>
            Fitur
          </span>
          <h2>Semua yang dibutuhkan <span class="hk-hl">rumah tangga modern</span></h2>
          <p class="hk-title__sub">Setiap fitur berguna sendiri-sendiri, dan jadi lebih kuat karena saling terhubung.</p>
        </div>
        <div class="hk-features__grid">
          <div v-for="(f, index) in features" :key="f.title" class="hk-feature animate-on-scroll"
            :class="'delay-' + ((index % 3) + 1)">
            <span v-if="f.soon" class="hk-feature__soon">Segera hadir</span>
            <div class="hk-feature__icon"><i :class="f.icon"></i></div>
            <h4>{{ f.title }}</h4>
            <div class="hk-feature__text">{{ f.text }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== SHOWCASE ===== -->
    <section id="tampilan" class="hk-section hk-section--linen">
      <div class="section-container">
        <div class="hk-title animate-on-scroll">
          <span class="hk-badge">
            <span class="hk-badge__dot"></span>
            Tampilan Aplikasi
          </span>
          <h2>Intip <span class="hk-hl">Homekind</span></h2>
          <p class="hk-title__sub">Ramah dipakai satu tangan, dengan bahasa sehari-hari, bukan bahasa bank.</p>
        </div>

        <div class="hk-showcase__grid animate-on-scroll">
          <div class="hk-showcase__tabs" role="tablist">
            <button v-for="(s, index) in showcase" :key="s.screen" type="button" role="tab" class="hk-showcase__tab"
              :class="{ 'hk-showcase__tab--active': activeShowcase === index }"
              :aria-selected="String(activeShowcase === index)" @click="activeShowcase = index">
              <span class="hk-showcase__tab-icon"><i :class="s.icon"></i></span>
              <span class="hk-showcase__tab-text">
                <strong>{{ s.title }}</strong>
                <span>{{ s.tagline }}</span>
              </span>
            </button>
          </div>

          <div class="hk-showcase__stage">
            <div class="hk-showcase__arch"></div>
            <transition name="hk-swap" mode="out-in">
              <PhoneMockup :key="currentShowcase.screen" :screen="currentShowcase.screen" />
            </transition>
          </div>

          <transition name="hk-swap" mode="out-in">
            <div :key="currentShowcase.screen" class="hk-showcase__detail">
              <h3>{{ currentShowcase.title }}</h3>
              <div class="hk-showcase__desc">{{ currentShowcase.description }}</div>
              <div class="hk-check-list">
                <div v-for="point in currentShowcase.points" :key="point" class="hk-check-list__item">
                  <i class="fa fa-check-circle"></i>
                  <span>{{ point }}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </section>

    <!-- ===== CONNECTED ===== -->
    <section class="hk-section hk-section--sage">
      <div class="section-container">
        <div class="hk-title animate-on-scroll">
          <span class="hk-badge">
            <span class="hk-badge__dot"></span>
            Saling Terhubung
          </span>
          <h2>Catat sekali, <span class="hk-hl">semuanya ikut terupdate</span></h2>
          <p class="hk-title__sub">Tidak perlu input berulang. Satu aksi otomatis mengalir ke modul lain.</p>
        </div>
        <div class="hk-connected__grid">
          <div v-for="(flow, index) in flows" :key="flow.trigger" class="hk-flow animate-on-scroll"
            :class="'delay-' + (index + 1)">
            <div class="hk-flow__trigger">
              <span class="hk-flow__icon"><i :class="flow.icon"></i></span>
              <strong>{{ flow.trigger }}</strong>
            </div>
            <div v-for="r in flow.results" :key="r" class="hk-flow__result">
              <i class="fa fa-long-arrow-right"></i>
              <span>{{ r }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== ROLES & PRIVACY ===== -->
    <section id="privasi" class="hk-section hk-section--forest">
      <div class="section-container">
        <div class="hk-privacy__grid">
          <div class="animate-on-scroll">
            <span class="hk-badge hk-badge--light">
              <span class="hk-badge__dot"></span>
              Peran &amp; Privasi
            </span>
            <h2>Dipakai bersama, <span class="hk-hl hk-hl--light">privasi tetap terjaga</span></h2>
            <p class="hk-privacy__lead">
              Rumah adalah ruang bersama, tapi tidak semua hal harus dilihat semua orang. Setiap anggota punya peran,
              dan setiap catatan punya pengaturan siapa yang boleh melihat.
            </p>
            <div class="hk-roles">
              <div v-for="role in roles" :key="role.name" class="hk-role">
                <i :class="role.icon"></i>
                <div>
                  <strong>{{ role.name }}</strong>
                  <span>{{ role.desc }}</span>
                </div>
              </div>
            </div>
            <div class="hk-trust">
              <div v-for="t in trust" :key="t" class="hk-trust__item">
                <i class="fa fa-shield"></i>
                <span>{{ t }}</span>
              </div>
            </div>
          </div>

          <div class="hk-privacy__side animate-on-scroll delay-2">
            <IlloHouseRooms class="hk-privacy__illo" />
            <div class="hk-visibility">
              <div v-for="v in visibility" :key="v.name" class="hk-visibility__item">
                <div class="hk-visibility__icon" :class="'hk-visibility__icon--' + v.tone"><i :class="v.icon"></i></div>
                <div>
                  <strong>{{ v.name }}</strong>
                  <span>{{ v.desc }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== PRICING ===== -->
    <section id="harga" class="hk-section hk-section--linen">
      <div class="section-container">
        <div class="hk-title animate-on-scroll">
          <span class="hk-badge">
            <span class="hk-badge__dot"></span>
            Harga
          </span>
          <h2>Satu langganan untuk <span class="hk-hl">satu rumah</span></h2>
          <p class="hk-title__sub">
            Bayar per rumah, bukan per orang. Mulai gratis, tingkatkan saat keluarga Anda bertambah.
          </p>
        </div>

        <div class="hk-pricing__grid">
          <div v-for="(plan, index) in plans" :key="plan.name" class="hk-plan animate-on-scroll"
            :class="['delay-' + (index + 1), { 'hk-plan--featured': plan.featured }]">
            <span v-if="plan.featured" class="hk-plan__ribbon">Paling lengkap</span>
            <div class="hk-plan__name">{{ plan.name }}</div>
            <div class="hk-plan__tagline">{{ plan.tagline }}</div>
            <div class="hk-plan__price">
              <strong>{{ plan.price }}</strong>
              <span>{{ plan.period }}</span>
            </div>
            <div class="hk-plan__note">{{ plan.note }}</div>
            <div class="hk-plan__seats">
              <i class="fa fa-users"></i> Hingga <strong>{{ plan.seats }} anggota</strong> per rumah
            </div>
            <div class="hk-plan__features">
              <div v-for="item in plan.items" :key="item.label" class="hk-plan__feature"
                :class="{ 'hk-plan__feature--off': !item.included }">
                <i :class="item.included ? 'fa fa-check' : 'fa fa-minus'"></i>
                <span>{{ item.label }}</span>
              </div>
            </div>
            <router-link :to="betaLink" class="hk-btn hk-btn--block"
              :class="plan.featured ? 'hk-btn--primary' : 'hk-btn--outline'">
              {{ plan.cta }}
            </router-link>
          </div>
        </div>
        <div class="hk-pricing__footnote animate-on-scroll">
          Kursi dihitung dari anggota aktif dan undangan yang belum diterima. Jika langganan berakhir, tidak ada data
          yang dihapus. Anggota di atas batas paket Gratis hanya bisa melihat sampai langganan diperpanjang.
        </div>
      </div>
    </section>

    <!-- ===== FAQ ===== -->
    <section id="faq" class="hk-section hk-section--linen-alt">
      <div class="section-container">
        <div class="hk-faq__grid animate-on-scroll">
          <div class="hk-faq__header">
            <span class="hk-badge">
              <span class="hk-badge__dot"></span>
              FAQ
            </span>
            <h2>Pertanyaan yang Sering <span class="hk-hl">Diajukan</span></h2>
            <p>
              Belum menemukan jawabannya? <router-link to="/contact" class="hk-inline-link">Hubungi kami</router-link>.
            </p>
          </div>
          <div>
            <div v-for="(faq, index) in faqs" :key="faq.question" class="accordion-item-custom hk-faq__item"
              :class="{ active: activeFaq === index }">
              <div class="accordion-header-custom" @click="toggleFaq(index)">
                <span>{{ faq.question }}</span>
                <span class="accordion-icon">+</span>
              </div>
              <div class="accordion-body-custom">
                <p>{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CTA ===== -->
    <section class="hk-cta">
      <div class="section-container">
        <div class="hk-cta__content animate-on-scroll">
          <HomekindLogo :size="56" :wordmark="false" class="hk-cta__logo" />
          <span class="hk-badge hk-badge--light">
            <span class="hk-badge__dot"></span>
            Kuota beta terbatas
          </span>
          <h2>Ajak seisi rumah mencoba <span class="hk-hl hk-hl--light">Homekind</span></h2>
          <p>
            Kami membuka Closed Beta untuk keluarga yang ingin ikut membentuk Homekind. Daftar sekarang dan dapatkan
            Family Plus gratis selama masa beta.
          </p>
          <div class="hk-cta__actions">
            <router-link :to="betaLink" class="hk-btn hk-btn--primary">
              Daftar Beta <i class="fa fa-arrow-right"></i>
            </router-link>
            <a href="https://wa.me/628114938580" class="hk-btn hk-btn--ghost-light">
              <i class="fa fa-whatsapp"></i> Tanya via WhatsApp
            </a>
          </div>
        </div>
      </div>
      <IlloNeighborhood class="hk-cta__houses" />
    </section>
  </div>
</template>

<script>
import PhoneMockup from "@/components/homekind/PhoneMockup";
import HomekindLogo from "@/components/homekind/HomekindLogo";
import IlloPlant from "@/components/homekind/illustrations/IlloPlant";
import IlloLamp from "@/components/homekind/illustrations/IlloLamp";
import IlloHouseRooms from "@/components/homekind/illustrations/IlloHouseRooms";
import IlloNeighborhood from "@/components/homekind/illustrations/IlloNeighborhood";

export default {
  name: "HomekindPage",
  components: { PhoneMockup, HomekindLogo, IlloPlant, IlloLamp, IlloHouseRooms, IlloNeighborhood },
  data() {
    return {
      betaLink: { path: "/contact", query: { subject: "Daftar Beta Homekind" } },
      activeShowcase: 0,
      activeFaq: 0,
      problems: [
        {
          icon: "fa fa-comments-o",
          title: "Info tenggelam di grup chat",
          text: "Jadwal, daftar belanja, dan pengingat hilang di antara ratusan pesan.",
        },
        {
          icon: "fa fa-bolt",
          title: "Tagihan sering telat",
          text: "PLN, PDAM, pajak kendaraan, PBB. Tidak ada yang merasa bertanggung jawab mengingatkan.",
        },
        {
          icon: "fa fa-exchange",
          title: "Uang bersama campur pribadi",
          text: "Sulit tahu siapa sudah bayar apa, dan siapa masih berutang ke siapa.",
        },
        {
          icon: "fa fa-user-times",
          title: "Aplikasi lain untuk satu orang",
          text: "Kebanyakan aplikasi dibuat untuk individu, bukan untuk seisi rumah.",
        },
      ],
      features: [
        {
          icon: "fa fa-pie-chart",
          title: "Kas & Anggaran",
          text: "Catat pemasukan dan pengeluaran, pisahkan uang bersama dan pribadi, atur anggaran per kategori dengan peringatan 80% dan 100%.",
        },
        {
          icon: "fa fa-calendar",
          title: "Agenda Keluarga",
          text: "Kalender bersama dengan acara berulang, RSVP, tugas rumah, ulang tahun, dan libur nasional.",
        },
        {
          icon: "fa fa-shopping-basket",
          title: "Daftar Belanja & Catatan",
          text: "Sinkron real-time dan tetap bisa dipakai offline. Selesai belanja, langsung tercatat sebagai pengeluaran.",
        },
        {
          icon: "fa fa-wrench",
          title: "Perawatan Rumah",
          text: "Jadwal servis AC, kuras toren, servis kendaraan, pajak STNK, dan PBB, lengkap dengan buku kontak tukang.",
        },
        {
          icon: "fa fa-handshake-o",
          title: "Utang & Piutang",
          text: "Lacak pinjaman antaranggota maupun ke pihak luar, cicilan, dan pengingat jatuh tempo yang sopan.",
          soon: true,
        },
        {
          icon: "fa fa-diamond",
          title: "Aset & Kekayaan Bersih",
          text: "Rumah, kendaraan, emas, reksa dana, deposito, dan polis asuransi beserta dokumennya dalam satu tempat.",
          soon: true,
        },
        {
          icon: "fa fa-book",
          title: "Jurnal Keluarga",
          text: "Abadikan momen dengan foto, tandai anggota, dan dapatkan kenangan \"hari ini di tahun lalu\".",
          soon: true,
        },
        {
          icon: "fa fa-bolt",
          title: "Bayar Tagihan",
          text: "Cek dan bayar PLN pascabayar, token listrik, dan PDAM dalam sekali ketuk via QRIS, VA, atau e-wallet.",
          soon: true,
        },
        {
          icon: "fa fa-comments",
          title: "Chat Keluarga",
          text: "Obrolan dengan kartu interaktif: RSVP acara, tandai tagihan lunas, atau centang daftar belanja langsung dari chat.",
          soon: true,
        },
        {
          icon: "fa fa-plane",
          title: "Trip & Itinerary",
          text: "Rencanakan liburan atau mudik: itinerary harian, anggaran trip, daftar bawaan, dan dokumen perjalanan.",
          soon: true,
        },
        {
          icon: "fa fa-gamepad",
          title: "Mini Games Keluarga",
          text: "Kuis keluarga, tebak gambar, dan sambung kata untuk malam main bersama, dari HP masing-masing.",
          soon: true,
        },
        {
          icon: "fa fa-bell",
          title: "Pengingat Pintar",
          text: "Notifikasi sesuai preferensi tiap anggota, dengan jam tenang dan tanpa isi sensitif di layar kunci.",
        },
      ],
      showcase: [
        {
          screen: "budget",
          icon: "fa fa-pie-chart",
          title: "Kas & Anggaran",
          tagline: "Tahu ke mana uang pergi",
          description:
            "Lihat pengeluaran bersama bulan ini, kategori mana yang hampir habis, dan transaksi yang masuk otomatis dari fitur lain.",
          points: [
            "Mode Bersama dan Pribadi dalam satu ketukan",
            "Periode anggaran mengikuti tanggal gajian",
            "Laporan per kategori, per anggota, dan arus kas",
          ],
        },
        {
          screen: "bills",
          icon: "fa fa-bolt",
          title: "Bayar Tagihan",
          tagline: "PLN & PDAM tanpa ribet",
          description:
            "Simpan ID pelanggan sekali, cek tagihan terbaru beserta nama pelanggan, lalu bayar. Struk dan pengeluaran tercatat otomatis.",
          points: [
            "Rincian biaya admin ditampilkan sebelum bayar",
            "Token listrik prabayar tersimpan di riwayat",
            "Pengingat 5, 2, dan 0 hari sebelum jatuh tempo",
          ],
        },
        {
          screen: "agenda",
          icon: "fa fa-calendar",
          title: "Agenda Keluarga",
          tagline: "Semua jadwal, satu kalender",
          description:
            "Jadwal sekolah, arisan, les, dan tugas rumah dalam satu kalender. Jatuh tempo tagihan dan servis ikut muncul.",
          points: [
            "RSVP Hadir / Mungkin / Tidak untuk setiap acara",
            "Libur nasional langsung tampil di kalender",
            "Sinkron ke Google Calendar atau Apple Calendar",
          ],
        },
        {
          screen: "shopping",
          icon: "fa fa-shopping-basket",
          title: "Daftar Belanja",
          tagline: "Real-time, bahkan offline",
          description:
            "Seisi rumah bisa menambah dan mencentang barang bersamaan. Asisten rumah tangga bisa diberi akses hanya ke daftar tertentu.",
          points: [
            "Lihat siapa yang mencentang, saat itu juga",
            "Tetap jalan tanpa sinyal di supermarket",
            "Selesai belanja → pengeluaran + foto struk",
          ],
        },
        {
          screen: "maintenance",
          icon: "fa fa-wrench",
          title: "Perawatan Rumah",
          tagline: "Tidak ada lagi yang terlewat",
          description:
            "Template perawatan khas rumah Indonesia sudah siap pakai. Jadwal berikutnya dihitung dari pengerjaan terakhir.",
          points: [
            "Servis AC, toren, pompa air, kendaraan, STNK, PBB",
            "Tugaskan ke anggota, asisten rumah tangga, atau tukang",
            "Hubungi tukang lewat WhatsApp sekali ketuk",
          ],
        },
        {
          screen: "chat",
          icon: "fa fa-comments",
          title: "Chat Keluarga",
          tagline: "Ngobrol sambil beres",
          description:
            "Bagikan tagihan, acara, atau daftar belanja ke chat sebagai kartu interaktif yang selalu menampilkan status terbaru.",
          points: [
            "RSVP dan tandai lunas langsung dari kartu",
            "Kanal khusus, misalnya hanya orang tua",
            "Kartu menghormati hak akses tiap anggota",
          ],
        },
      ],
      flows: [
        {
          icon: "fa fa-snowflake-o",
          trigger: "Servis AC selesai",
          results: ["Biaya masuk ke Kas & Anggaran", "Jadwal servis berikutnya dihitung ulang", "Agenda ikut diperbarui"],
        },
        {
          icon: "fa fa-shopping-basket",
          trigger: "Selesai belanja",
          results: ["Pengeluaran Belanja Dapur tercatat", "Foto struk tersimpan", "Barang tercentang diarsipkan"],
        },
        {
          icon: "fa fa-plane",
          trigger: "Trip mudik dibuat",
          results: ["Blok hari di Agenda", "Anggaran trip & daftar bawaan", "Grup chat khusus peserta"],
        },
        {
          icon: "fa fa-bolt",
          trigger: "Tagihan PLN dibayar",
          results: ["Pengeluaran Listrik tercatat", "Jatuh tempo di Agenda ditandai selesai", "Struk dikirim ke pembayar"],
        },
      ],
      roles: [
        { icon: "fa fa-star", name: "Pemilik & Admin", desc: "Atur anggota, keuangan, dan langganan" },
        { icon: "fa fa-user", name: "Dewasa", desc: "Akses penuh ke semua fitur harian" },
        { icon: "fa fa-graduation-cap", name: "Remaja", desc: "Uang saku dan pengeluaran sendiri" },
        { icon: "fa fa-child", name: "Anak", desc: "Agenda, tugas, catatan, dan games" },
        { icon: "fa fa-id-badge", name: "Asisten Rumah Tangga & Sopir", desc: "Daftar belanja dan tugas yang ditugaskan" },
        { icon: "fa fa-user-plus", name: "Tamu", desc: "Hanya trip atau acara yang dibagikan" },
      ],
      visibility: [
        { icon: "fa fa-lock", name: "Pribadi", tone: "linen", desc: "Hanya Anda. Default untuk jurnal dan utang." },
        { icon: "fa fa-user-circle", name: "Anggota tertentu", tone: "sage", desc: "Pilih siapa saja yang boleh melihat." },
        { icon: "fa fa-home", name: "Seisi rumah", tone: "apricot", desc: "Terlihat oleh semua anggota yang berhak." },
      ],
      trust: [
        "Homekind tidak pernah menyimpan uang Anda. Pembayaran lewat mitra berlisensi.",
        "Data terenkripsi dan setiap rumah terisolasi satu sama lain.",
        "Notifikasi tidak menampilkan isi sensitif di layar kunci.",
        "Dirancang mengikuti UU Pelindungan Data Pribadi.",
      ],
      plans: [
        {
          name: "Gratis",
          tagline: "Untuk keluarga kecil yang baru mulai",
          price: "Rp0",
          period: "selamanya",
          note: "Tanpa kartu kredit.",
          seats: 4,
          cta: "Mulai Gratis",
          items: [
            { label: "Kas, anggaran, agenda, daftar & perawatan", included: true },
            { label: "Sinkron real-time & mode offline", included: true },
            { label: "Penyimpanan foto & dokumen 2 GB", included: true },
            { label: "Ekspor riwayat hingga 3 bulan", included: true },
            { label: "Bayar tagihan otomatis", included: false },
            { label: "Video di jurnal keluarga", included: false },
          ],
        },
        {
          name: "Family Plus",
          tagline: "Untuk keluarga besar, asisten rumah tangga, dan kakek-nenek",
          price: "Rp199.000",
          period: "/ rumah / tahun",
          note: "Setara ±Rp17 ribu per bulan untuk seisi rumah.",
          seats: 20,
          featured: true,
          cta: "Coba Gratis Selama Beta",
          items: [
            { label: "Semua fitur paket Gratis", included: true },
            { label: "Penyimpanan foto & dokumen 50 GB", included: true },
            { label: "Riwayat & ekspor tanpa batas (CSV/XLSX)", included: true },
            { label: "Bayar tagihan otomatis dengan batas nominal", included: true },
            { label: "Mini games & video di jurnal", included: true },
            { label: "Prioritas dukungan", included: true },
          ],
        },
      ],
      faqs: [
        {
          question: "Kapan Homekind bisa digunakan?",
          answer:
            "Homekind sedang memasuki Closed Beta bersama keluarga-keluarga pertama. Daftar sekarang untuk mendapat undangan. Fitur dirilis bertahap, dimulai dari Agenda, Daftar Belanja, Perawatan Rumah, dan Kas & Anggaran.",
        },
        {
          question: "Apakah Homekind menyimpan uang saya?",
          answer:
            "Tidak. Homekind hanya mencatat keuangan Anda. Saat membayar tagihan, pembayaran diproses oleh payment gateway dan mitra PPOB berlisensi, dan Homekind tidak pernah menampung dana Anda.",
        },
        {
          question: "Siapa yang bisa melihat pengeluaran pribadi saya?",
          answer:
            "Hanya Anda. Setiap catatan punya pengaturan visibilitas: Pribadi, Anggota tertentu, atau Seisi rumah. Total dan laporan juga hanya dihitung dari catatan yang boleh dilihat oleh orang yang membukanya.",
        },
        {
          question: "Bisakah asisten rumah tangga atau sopir ikut diundang?",
          answer:
            "Bisa. Peran Asisten Rumah Tangga & Sopir hanya bisa mengakses daftar belanja, tugas yang ditugaskan, dan agenda yang dibagikan, tanpa akses ke keuangan, jurnal, utang, maupun aset.",
        },
        {
          question: "Bisakah saya bergabung di lebih dari satu rumah?",
          answer:
            "Bisa. Satu akun dapat menjadi anggota di beberapa rumah, misalnya rumah sendiri dan rumah orang tua, lalu berpindah dengan sekali ketuk.",
        },
        {
          question: "Apa yang terjadi jika langganan Family Plus berakhir?",
          answer:
            "Tidak ada data yang dihapus. Ada masa tenggang 7 hari, lalu anggota di atas batas 4 orang (yang paling baru bergabung) menjadi hanya-lihat sampai langganan diperpanjang.",
        },
      ],
    };
  },
  computed: {
    currentShowcase() {
      return this.showcase[this.activeShowcase];
    },
  },
  mounted() {
    this.initScrollAnimations();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    initScrollAnimations() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animated");
            }
          });
        },
        { threshold: 0.1 }
      );
      this.$nextTick(() => {
        this.$el.querySelectorAll(".animate-on-scroll").forEach((el) => {
          this.observer.observe(el);
        });
      });
    },
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? -1 : index;
    },
  },
};
</script>

<style scoped lang="scss">
// ===== BASE =====
.hk-page {
  background: $hk-linen;
  color: $hk-muted;

  h1,
  h2,
  h3,
  h4 {
    font-family: $hk-font-heading;
    font-weight: 800;
    color: $hk-ink;
    letter-spacing: -0.3px;
  }
}

.hk-section {
  position: relative;
  padding: $section-padding-y 0;
  overflow: hidden;

  &--linen {
    background: $hk-linen;
  }

  &--linen-alt {
    background: $hk-linen-alt;
  }

  &--sage {
    background: $hk-sage-soft;
  }

  &--forest {
    background: $hk-forest;
    color: rgba(246, 241, 231, 0.75);

    h2,
    h3,
    h4 {
      color: $hk-linen;
    }
  }
}

// Marker-pen highlight, like a note on the fridge
.hk-hl {
  color: $hk-apricot-deep;
  background: linear-gradient(transparent 64%, rgba(224, 145, 95, 0.25) 64%, rgba(224, 145, 95, 0.25) 92%, transparent 92%);
  padding: 0 2px;

  &--light {
    color: $hk-apricot-light;
    background: linear-gradient(transparent 64%, rgba(242, 185, 143, 0.18) 64%, rgba(242, 185, 143, 0.18) 92%, transparent 92%);
  }
}

.hk-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  margin-bottom: 18px;
  border-radius: 50px;
  background: $hk-sage-soft;
  border: 1px solid rgba(107, 143, 113, 0.25);
  color: $hk-forest;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;

  &--light {
    background: rgba(246, 241, 231, 0.1);
    border-color: rgba(246, 241, 231, 0.18);
    color: $hk-linen;
  }
}

.hk-badge__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $hk-apricot;
}

.hk-title {
  text-align: center;
  margin-bottom: 56px;
}

.hk-title__sub {
  max-width: 600px;
  margin: 12px auto 0;
  font-size: 1.05rem;
  color: $hk-muted;
}

.hk-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 30px;
  border-radius: 50px;
  border: 2px solid transparent;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all $transition-base;

  i {
    transition: transform $transition-fast;
  }

  &:hover i.fa-arrow-right {
    transform: translateX(4px);
  }

  &--block {
    display: flex;
    width: 100%;
  }
}

// Vuetify colours every <a>; these keep button text readable
.v-application a.hk-btn--primary {
  background: $hk-apricot;
  color: $hk-forest-deep !important;
  box-shadow: 0 8px 22px rgba(224, 145, 95, 0.35);

  &:hover {
    background: $hk-apricot-light;
    transform: translateY(-2px);
  }
}

.v-application a.hk-btn--ghost-light {
  color: $hk-linen !important;
  border-color: rgba(246, 241, 231, 0.3);

  &:hover {
    border-color: rgba(246, 241, 231, 0.7);
    background: rgba(246, 241, 231, 0.06);
  }
}

.v-application a.hk-btn--outline {
  color: $hk-forest !important;
  border-color: $hk-forest;

  &:hover {
    background: $hk-forest;
    color: $hk-linen !important;
  }
}

.v-application a.hk-inline-link {
  color: $hk-apricot-deep;
  font-weight: 700;
}

// ===== HERO =====
.hk-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 120px 0 140px;
  overflow: hidden;
  background: radial-gradient(ellipse at 75% 40%, #3d5f4a 0%, $hk-forest 55%, $hk-forest-deep 100%);
}

.hk-hero__blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;

  &--1 {
    top: -10%;
    right: 5%;
    width: 520px;
    height: 520px;
    background: rgba(242, 185, 143, 0.14);
  }

  &--2 {
    bottom: -25%;
    left: -10%;
    width: 480px;
    height: 480px;
    background: rgba(169, 196, 165, 0.14);
  }
}

.hk-hero__grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 48px;
  align-items: center;
}

.hk-page .hk-hero__title {
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1.1;
  color: $hk-linen;
  margin-bottom: 20px;
}

.hk-hero__subtitle {
  max-width: 520px;
  margin-bottom: 32px;
  font-size: 1.05rem;
  line-height: 1.8;
  color: rgba(246, 241, 231, 0.72);
}

.hk-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 34px;
}

.hk-hero__platforms {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 14px;
    background: rgba(246, 241, 231, 0.07);
    border: 1px solid rgba(246, 241, 231, 0.12);
    color: rgba(246, 241, 231, 0.85);
    font-size: 0.85rem;
    font-weight: 500;
  }
}

.hk-hero__visual {
  position: relative;
  height: 660px;
}

// Arched window behind the phones
.hk-hero__arch {
  position: absolute;
  top: 40px;
  left: 2%;
  width: 380px;
  height: 600px;
  border-radius: 190px 190px 28px 28px;
  background: linear-gradient(180deg, rgba(246, 241, 231, 0.13), rgba(246, 241, 231, 0.04));
  border: 1px solid rgba(246, 241, 231, 0.12);
}

// Hangs left of the phones so its cord falls between the logo and the nav links
.hk-hero__lamp {
  position: absolute;
  top: -150px;
  left: -90px;
  width: 150px;
  z-index: 1;
}

.hk-hero__plant {
  position: absolute;
  bottom: -10px;
  left: -70px;
  width: 150px;
  z-index: 3;
}

.hk-hero__phone {
  position: absolute;

  &--front {
    top: 20px;
    left: 10%;
    z-index: 2;
  }

  &--back {
    top: 90px;
    right: 0;
    z-index: 1;
    opacity: 0.8;
    transform: rotate(6deg);
  }
}

.hk-float {
  position: absolute;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 16px;
  background: rgba(246, 241, 231, 0.96);
  box-shadow: $hk-shadow-lg;
  animation: hk-float 4s ease-in-out infinite;

  strong {
    display: block;
    color: $hk-ink;
    font-size: 0.85rem;
  }

  span {
    color: $hk-muted;
    font-size: 0.75rem;
  }

  &--1 {
    bottom: 30px;
    left: 40px;
  }

  &--2 {
    top: 40px;
    right: -10px;
    animation-delay: 2s;
  }
}

.hk-float__icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: $hk-sage-mid;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--alt {
    background: $hk-apricot;
  }
}

@keyframes hk-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

.hk-wave {
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 100%;
  height: 80px;
  z-index: 3;

  path {
    fill: $hk-linen;
  }
}

// ===== PROBLEM =====
.hk-problem__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.hk-problem {
  padding: 28px 24px;
  border-radius: 24px;
  background: #fff;
  box-shadow: $hk-shadow-sm;

  h4 {
    font-size: 1.1rem;
    margin-bottom: 8px;
  }
}

.hk-problem__icon {
  width: 52px;
  height: 52px;
  margin-bottom: 18px;
  border-radius: 18px 18px 18px 6px;
  background: $hk-apricot-soft;
  color: $hk-apricot-deep;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.hk-problem__text {
  font-size: 0.92rem;
  line-height: 1.65;
}

// ===== FEATURES =====
.hk-features__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.hk-feature {
  position: relative;
  padding: 30px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid $hk-line;
  transition: all $transition-base;

  h4 {
    font-size: 1.15rem;
    margin-bottom: 10px;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: $hk-shadow-md;

    .hk-feature__icon {
      background: $hk-forest;
      color: $hk-linen;
    }
  }
}

.hk-feature__icon {
  width: 58px;
  height: 58px;
  margin-bottom: 20px;
  border-radius: 20px;
  background: $hk-sage-soft;
  color: $hk-forest;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  transition: all $transition-base;
}

.hk-feature__text {
  font-size: 0.93rem;
  line-height: 1.7;
}

.hk-feature__soon {
  position: absolute;
  top: 26px;
  right: 24px;
  padding: 3px 10px;
  border-radius: 50px;
  background: $hk-apricot-soft;
  color: $hk-apricot-deep;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

// ===== SHOWCASE =====
.hk-showcase__grid {
  display: grid;
  grid-template-columns: 300px 1fr 320px;
  gap: 40px;
  align-items: center;
}

.hk-showcase__tabs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hk-showcase__tab {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid transparent;
  border-radius: 18px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: all $transition-base;

  &:hover {
    background: rgba(255, 255, 255, 0.6);
  }

  &--active {
    background: #fff;
    border-color: $hk-line;
    box-shadow: $hk-shadow-md;

    .hk-showcase__tab-icon {
      background: $hk-forest;
      color: $hk-linen;
    }
  }
}

.hk-showcase__tab-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: $hk-sage-soft;
  color: $hk-forest;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all $transition-base;
}

.hk-showcase__tab-text {
  display: flex;
  flex-direction: column;

  strong {
    font-family: $hk-font-heading;
    color: $hk-ink;
    font-size: 1rem;
  }

  span {
    color: $hk-muted;
    font-size: 0.8rem;
  }
}

.hk-showcase__stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 660px;
}

.hk-showcase__arch {
  position: absolute;
  width: 360px;
  height: 620px;
  border-radius: 180px 180px 32px 32px;
  background: $hk-sage-soft;
  border: 1px solid rgba(107, 143, 113, 0.2);
}

.hk-showcase__detail h3 {
  margin-bottom: 12px;
  font-size: 1.7rem;
}

.hk-showcase__desc {
  margin-bottom: 20px;
  line-height: 1.75;
}

.hk-check-list__item {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.93rem;

  i {
    color: $hk-sage;
    margin-top: 4px;
  }
}

.hk-swap-enter-active,
.hk-swap-leave-active {
  transition: all 0.25s ease;
}

.hk-swap-enter {
  opacity: 0;
  transform: translateY(12px);
}

.hk-swap-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

// ===== CONNECTED =====
.hk-connected__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.hk-flow {
  padding: 24px;
  border-radius: 24px;
  background: #fff;
  box-shadow: $hk-shadow-sm;
}

.hk-flow__trigger {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  margin-bottom: 14px;
  border-bottom: 2px dashed $hk-line;

  strong {
    font-family: $hk-font-heading;
    color: $hk-ink;
    font-size: 1.05rem;
  }
}

.hk-flow__icon {
  width: 44px;
  height: 44px;
  border-radius: 16px 16px 16px 6px;
  background: $hk-apricot;
  color: $hk-forest-deep;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hk-flow__result {
  display: flex;
  gap: 10px;
  padding: 5px 0;
  font-size: 0.9rem;

  i {
    color: $hk-sage;
    margin-top: 5px;
  }
}

// ===== PRIVACY =====
.hk-privacy__grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 60px;
  align-items: center;
}

.hk-privacy__lead {
  margin: 16px 0 28px;
  line-height: 1.8;
}

.hk-roles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 28px;
}

.hk-role {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(246, 241, 231, 0.06);
  border: 1px solid rgba(246, 241, 231, 0.1);

  i {
    width: 16px;
    margin-top: 4px;
    text-align: center;
    color: $hk-apricot-light;
  }

  strong {
    display: block;
    color: $hk-linen;
    font-size: 0.92rem;
  }

  span {
    color: rgba(246, 241, 231, 0.6);
    font-size: 0.8rem;
  }
}

.hk-trust__item {
  display: flex;
  gap: 12px;
  padding: 6px 0;
  font-size: 0.9rem;
  color: rgba(246, 241, 231, 0.78);

  i {
    color: $hk-sage-light;
    margin-top: 4px;
  }
}

.hk-privacy__illo {
  display: block;
  width: 100%;
  max-width: 420px;
  margin: 0 auto 12px;
}

.hk-visibility {
  padding: 8px;
  border-radius: 24px;
  background: rgba(246, 241, 231, 0.06);
  border: 1px solid rgba(246, 241, 231, 0.1);
}

.hk-visibility__item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;

  & + & {
    border-top: 1px solid rgba(246, 241, 231, 0.08);
  }

  strong {
    display: block;
    color: $hk-linen;
  }

  span {
    color: rgba(246, 241, 231, 0.6);
    font-size: 0.85rem;
  }
}

// Tile colours match the rooms in the house illustration
.hk-visibility__icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: $hk-forest;
  font-size: 1.05rem;

  &--linen {
    background: $hk-linen-alt;
  }

  &--sage {
    background: $hk-sage-soft;
  }

  &--apricot {
    background: $hk-apricot-soft;
    color: $hk-apricot-deep;
  }
}

// ===== PRICING =====
.hk-pricing__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 420px));
  justify-content: center;
  gap: 28px;
}

.hk-plan {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 36px 32px;
  border-radius: 28px;
  background: #fff;
  border: 1px solid $hk-line;
  box-shadow: $hk-shadow-sm;

  &--featured {
    background: $hk-forest;
    border-color: transparent;
    box-shadow: $hk-shadow-lg;

    .hk-plan__name,
    .hk-plan__price strong {
      color: $hk-linen;
    }

    .hk-plan__tagline,
    .hk-plan__price span,
    .hk-plan__note {
      color: rgba(246, 241, 231, 0.65);
    }

    .hk-plan__seats {
      background: rgba(246, 241, 231, 0.08);
      color: rgba(246, 241, 231, 0.9);
    }

    .hk-plan__feature {
      color: rgba(246, 241, 231, 0.85);

      i {
        color: $hk-apricot-light;
      }
    }
  }
}

.hk-plan__ribbon {
  position: absolute;
  top: -14px;
  right: 28px;
  padding: 6px 16px;
  border-radius: 50px;
  background: $hk-apricot;
  color: $hk-forest-deep;
  font-size: 0.75rem;
  font-weight: 800;
  box-shadow: 0 6px 18px rgba(224, 145, 95, 0.4);
}

.hk-plan__name {
  font-family: $hk-font-heading;
  font-size: 1.5rem;
  font-weight: 900;
  color: $hk-ink;
}

.hk-plan__tagline {
  margin-top: 4px;
  font-size: 0.9rem;
}

.hk-plan__price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;

  strong {
    font-family: $hk-font-heading;
    font-size: 2.7rem;
    font-weight: 900;
    line-height: 1;
    color: $hk-ink;
  }

  span {
    font-size: 0.9rem;
  }
}

.hk-plan__note {
  margin-top: 8px;
  font-size: 0.82rem;
}

.hk-plan__seats {
  margin: 20px 0;
  padding: 12px 16px;
  border-radius: 14px;
  background: $hk-linen;
  font-size: 0.9rem;

  i {
    margin-right: 6px;
    color: $hk-apricot-deep;
  }
}

.hk-plan__features {
  flex: 1;
  margin-bottom: 28px;
}

.hk-plan__feature {
  display: flex;
  gap: 12px;
  padding: 7px 0;
  font-size: 0.9rem;

  i {
    width: 14px;
    margin-top: 5px;
    color: $hk-sage;
  }

  &--off {
    opacity: 0.45;

    i {
      color: inherit;
    }
  }
}

.hk-pricing__footnote {
  max-width: 720px;
  margin: 32px auto 0;
  text-align: center;
  font-size: 0.85rem;
}

// ===== FAQ =====
.hk-faq__grid {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 60px;
  align-items: start;
}

.hk-faq__header {
  position: sticky;
  top: 120px;

  p {
    margin-top: 16px;
  }
}

.hk-faq__item {
  border-color: $hk-line;
  border-radius: 18px !important;

  &.active {
    border-color: rgba(107, 143, 113, 0.35);
  }

  .accordion-header-custom {
    font-family: $hk-font-heading;
    font-weight: 800;
    color: $hk-ink;

    &:hover {
      color: $hk-sage-mid;
    }
  }

  .accordion-icon {
    background: $hk-sage-soft;
    color: $hk-forest;
  }

  &.active .accordion-icon {
    background: $hk-forest;
    color: $hk-linen;
  }

  .accordion-body-custom {
    color: $hk-muted;
  }
}

// ===== CTA =====
.hk-cta {
  position: relative;
  padding: 100px 0 230px;
  overflow: hidden;
  background: linear-gradient(180deg, $hk-forest-night 0%, $hk-forest 70%);
}

.hk-cta__content {
  position: relative;
  z-index: 2;
  max-width: 700px;
  margin: 0 auto;
  text-align: center;

  h2 {
    color: $hk-linen;
    margin-bottom: 16px;
  }

  p {
    margin-bottom: 32px;
    color: rgba(246, 241, 231, 0.7);
  }
}

.hk-cta__logo {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
}

.hk-cta__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
}

.hk-cta__houses {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 240px;
}

// ===== RESPONSIVE =====
@media (max-width: 1199px) {
  .hk-showcase__grid {
    grid-template-columns: 260px 1fr;
  }

  .hk-showcase__detail {
    grid-column: 1 / -1;
    max-width: 640px;
    margin: 0 auto;
    text-align: center;
  }

  .hk-check-list {
    display: inline-block;
    text-align: left;
  }

  .hk-connected__grid,
  .hk-problem__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .hk-hero__plant {
    left: -30px;
  }
}

@media (max-width: 991px) {
  .hk-section {
    padding: $section-padding-y-sm 0;
  }

  .hk-hero {
    min-height: auto;
    padding: 120px 0 110px;
  }

  .hk-hero__grid,
  .hk-privacy__grid,
  .hk-faq__grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hk-page .hk-hero__title {
    font-size: 2.6rem;
  }

  .hk-hero__visual {
    width: 100%;
    max-width: 520px;
    margin: 0 auto;
  }

  .hk-features__grid {
    grid-template-columns: 1fr 1fr;
  }

  .hk-showcase__grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .hk-showcase__tabs {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 8px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .hk-showcase__tab {
    flex-shrink: 0;
  }

  .hk-showcase__tab-text span {
    display: none;
  }

  .hk-faq__header {
    position: static;
  }
}

@media (max-width: 767px) {
  .hk-page .hk-hero__title {
    font-size: 2.1rem;
  }

  .hk-hero__visual {
    height: 650px;
  }

  .hk-hero__phone--back,
  .hk-float,
  .hk-hero__lamp {
    display: none;
  }

  .hk-hero__arch {
    left: 50%;
    transform: translateX(-50%);
    width: 330px;
  }

  .hk-hero__phone--front {
    left: 50%;
    transform: translateX(-50%);
  }

  .hk-hero__plant {
    left: -10px;
    width: 110px;
  }

  .hk-features__grid,
  .hk-connected__grid,
  .hk-problem__grid,
  .hk-roles {
    grid-template-columns: 1fr;
  }

  .hk-pricing__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .hk-plan {
    padding: 32px 24px;
  }

  .hk-showcase__stage {
    min-height: 0;
    padding: 20px 0;
  }

  .hk-showcase__arch {
    width: 320px;
    height: 100%;
  }

  .hk-cta {
    padding-bottom: 180px;
  }

  .hk-cta__houses {
    height: 170px;
  }
}
</style>
